import type { CatalogoClasses, ProdutoAnalise, ResultadoIndividual, StatusLote, SugestaoClasses } from './types'

/**
 * Chama o servico_marca (services/marca no fiestaup). Same-origin, mesmo padrão do
 * servico_buybox — sessão S2 autentica via nginx auth_request, POSTs levam X-Token
 * injetado só no servidor.
 */
export class MarcaApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.name = 'MarcaApiError'
    this.status = status
  }
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  let resp: Response
  try {
    resp = await fetch(path, { credentials: 'include', ...init })
  } catch {
    throw new Error(
      'Serviço de Central de Marcas inacessível. Se acabámos de activar, confirma o proxy e o processo fiesta-marcas.',
    )
  }
  if (!resp.ok) {
    const corpo = (await resp.json().catch(() => null)) as {
      erro?: string
      detail?: string | { msg?: string }
      message?: string
    } | null
    const detail =
      typeof corpo?.detail === 'string'
        ? corpo.detail
        : corpo?.detail && typeof corpo.detail === 'object'
          ? corpo.detail.msg
          : undefined
    throw new MarcaApiError(
      resp.status,
      corpo?.erro ||
        detail ||
        corpo?.message ||
        `Falha na chamada ao serviço de Central de Marcas (HTTP ${resp.status}).`,
    )
  }
  return resp.json() as Promise<T>
}

const SEM_PRODUTO: ProdutoAnalise = { produto: '', classes: [], principais: [] }

/** Produto + classes vão juntos: o serviço guarda essa seleção pra próxima vez que o mesmo
 * produto for digitado. `classes` vazio = regra geral (qualquer classe de produto ativa é alto). */
function corpoProduto(p: ProdutoAnalise) {
  return { produto: p.produto, classes_produto: p.classes, classes_principais: p.principais }
}

export function consultarMarca(termo: string, produto: ProdutoAnalise = SEM_PRODUTO): Promise<ResultadoIndividual> {
  return api<ResultadoIndividual>('/api/marcas/consultar', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ termo, ...corpoProduto(produto) }),
  })
}

export function iniciarConsultaLote(
  marcas: string[],
  produto: ProdutoAnalise = SEM_PRODUTO,
): Promise<{ ok: true; total: number }> {
  return api('/api/marcas/lote', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ marcas, ...corpoProduto(produto) }),
  })
}

/** As 45 classes de Nice, pra escolha manual das classes do produto. */
export function fetchClassesNice(): Promise<CatalogoClasses> {
  return api<CatalogoClasses>('/api/marcas/classes')
}

/** Pede ao serviço as classes de Nice do produto. Ele resolve pela fonte mais barata:
 * escolha já confirmada → tabela de produtos conhecidos → Claude Haiku do painel (com cache).
 * `ok: false` não é erro de tela: a IA pode estar sem chave/cota e a escolha segue manual. */
export function sugerirClassesProduto(produto: string): Promise<SugestaoClasses> {
  return api<SugestaoClasses>('/api/marcas/classes-produto', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ produto }),
  })
}

export function fetchStatusLote(): Promise<StatusLote> {
  return api<StatusLote>('/api/marcas/lote/status')
}

export type MarcasAnalise = {
  ok: boolean
  texto?: string
  leitura?: string
  modelo?: string
  fonte?: string
  gerado_em?: string
  cached?: boolean
  erro?: string
  error?: string
  message?: string
}

export async function fetchMarcasAnalise(): Promise<MarcasAnalise> {
  const resp = await fetch('/api/bridge/marcas/analise', {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  const data = (await resp.json().catch(() => ({}))) as MarcasAnalise
  if (!resp.ok && !data.ok) {
    return {
      ok: false,
      erro:
        data.erro ||
        data.message ||
        data.error ||
        `Falha na leitura IA (HTTP ${resp.status}).`,
    }
  }
  return data
}
